import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faBriefcase, faCubes, faScroll, faFileContract, faWallet, faHandshake, faChartLine, faStore, faBoxOpen, faTags } from '@fortawesome/free-solid-svg-icons';
import { faUser, faEye } from '@fortawesome/free-regular-svg-icons';
import { faDebian } from '@fortawesome/free-brands-svg-icons';


import { useAuth } from '../../hooks/useAuth';
import '../../styles/DashboardLayout.css';

const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = useMemo(() => {
    const baseItems = [];

    if (user?.role === 'superadmin') {
      return [
        ...baseItems,
        { path: '/superadmin/dashboard', label: 'Dashboard', icon: <FontAwesomeIcon icon={faCubes} /> },
        { path: '/superadmin/dashboard/users', label: 'Users', icon: <FontAwesomeIcon icon={faUser} /> },
        { path: '/superadmin/dashboard/segmentation', label: 'Segmentation', icon: <FontAwesomeIcon icon={faLayerGroup} /> },
        { path: '/superadmin/dashboard/pricing-rules', label: 'Pricing Rules', icon: <FontAwesomeIcon icon={faTags} /> },
        { path: '/superadmin/dashboard/wallet', label: 'Wallet', icon: <FontAwesomeIcon icon={faWallet} /> },
      ];
    }

    if (user?.role === 'manager') {
      return [
        ...baseItems,
        { path: '/manager/dashboard', label: 'Dashboard', icon: <FontAwesomeIcon icon={faCubes} /> },
        { path: '/manager/dashboard/contracts', label: 'Contracts', icon: <FontAwesomeIcon icon={faFileContract} /> },
        { path: '/manager/dashboard/accords', label: 'Investor Accords', icon: <FontAwesomeIcon icon={faHandshake} /> },
        { path: '/manager/dashboard/tittat-application', label: 'TitTat Application', icon: <FontAwesomeIcon icon={faBriefcase} /> },
        { path: '/manager/dashboard/tittats', label: 'Tit Tats', icon: <FontAwesomeIcon icon={faDebian} /> },
        { path: '/manager/dashboard/live-bid-sessions', label: 'Live Bid Sessions', icon: <FontAwesomeIcon icon={faScroll} /> },
        { path: '/manager/dashboard/wallet', label: 'Wallet', icon: <FontAwesomeIcon icon={faWallet} /> },
      ];
    }

    if (user?.role === 'artist') {
      return [
        ...baseItems,
        { path: '/artist/dashboard', label: 'Dashboard', icon: <FontAwesomeIcon icon={faCubes} /> },
        { path: '/artist/dashboard/contracts', label: 'Contracts', icon: <FontAwesomeIcon icon={faFileContract} /> },
        { path: '/artist/dashboard/my-artworks', label: 'Gallery', icon: <FontAwesomeIcon icon={faLayerGroup} /> },
        { path: '/artist/dashboard/tittat-application', label: 'TitTat Application', icon: <FontAwesomeIcon icon={faBriefcase} /> },
        { path: '/artist/dashboard/tittats', label: 'Tit Tats', icon: <FontAwesomeIcon icon={faDebian} /> },
        { path: '/artist/dashboard/my-shop', label: 'My Shop', icon: <FontAwesomeIcon icon={faStore} /> },
        { path: '/artist/dashboard/shop-orders', label: 'Shop Orders', icon: <FontAwesomeIcon icon={faBoxOpen} /> },
        { path: '/artist/dashboard/wallet', label: 'Wallet', icon: <FontAwesomeIcon icon={faWallet} /> },
      ];
    }

    if (user?.role === 'investor') {
      return [
        ...baseItems,
        { path: '/investor/dashboard', label: 'Dashboard', icon: <FontAwesomeIcon icon={faCubes} /> },
        { path: '/investor/dashboard/holdings', label: 'My Holdings', icon: <FontAwesomeIcon icon={faLayerGroup} /> },
        { path: '/investor/dashboard/accords', label: 'Accords', icon: <FontAwesomeIcon icon={faHandshake} /> },
        { path: '/investor/dashboard/trading', label: 'Trading', icon: <FontAwesomeIcon icon={faChartLine} /> },
        { path: '/investor/dashboard/bid-sessions', label: 'TitTats Auction', icon: <FontAwesomeIcon icon={faScroll} /> },
        { path: '/investor/dashboard/wallet', label: 'Wallet', icon: <FontAwesomeIcon icon={faWallet} /> },
      ];
    }

    return baseItems;
  }, [user]);

  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (sidebarOpen && !event.target.closest('.sidebar') && !event.target.closest('.menu-toggle')) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [sidebarOpen]);

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button className="close-sidebar" onClick={toggleSidebar}>
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-text">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="dashboard-content">
        <div className="mobile-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <FontAwesomeIcon icon={faEye} />
          </button>
          <div className="mobile-title">{navItems.find((i) => i.path === location.pathname)?.label}</div>
        </div>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;